import React, { useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import {
  IonIcon,
  IonChip,
  IonLabel,
  IonSpinner,
  IonButton,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IonText,
} from "@ionic/react";
import { FileMetadata } from "./PymbleFileUploader";
import {
  bagHandleOutline,
  closeOutline,
  documentOutline,
  documentTextOutline,
  imageOutline,
  receiptOutline,
  tvOutline,
  videocamOutline,
} from "ionicons/icons";

import { useFileUtils } from "../../hooks/useFileUtils";

import "./FileAttachment.css";

export type FileAttachmentButtonType = "button" | "chip";

interface FileAttachmentProps extends RouteComponentProps<any> {
  file: FileMetadata;
  label?: string;
  icon?: string;
  size?: 'small' | 'default' | 'large';
  onDelete?: (file: FileMetadata) => void;
  buttonType?: FileAttachmentButtonType;
  cssClassName?: string;
}

/**
 * Determine the icon for the file type
 *
 * @param file : FileMetadata
 * @returns icon for the file type
 */
const getFileIcon = (file: FileMetadata) => {
  const ext = file?.Name?.split(".").pop()?.toLowerCase();
  const type = file?.Type?.split("/").shift()?.toLowerCase();
  switch (type) {
    case "image":
      return imageOutline;
    case "video":
      return videocamOutline;
    case "text":
      return documentTextOutline;
  }

  switch (ext) {
    case "pdf":
      return documentOutline;
    case "doc":
    case "docx":
      return documentOutline;
    case "xls":
    case "xlsx":
      return receiptOutline;
    case "ppt":
    case "pptx":
      return tvOutline;
    case "zip":
      return bagHandleOutline;
    default:
      return documentOutline;
  }
};

// eslint-disable-next-line react-refresh/only-export-components
const FileAttachment: React.FC<FileAttachmentProps> = ({
  file,
  onDelete,
  label,
  icon,
  size,
  buttonType,
  cssClassName,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { deleteFile, saveAndOpenFile } = useFileUtils();
  const [downloading, setDownloading] = useState(false);

  const handleFileOpen = async (e: any, file: FileMetadata) => {
    console.log("Opening file", file);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    e.preventDefault && e.preventDefault();
    setDownloading(true);
    await saveAndOpenFile(file);
    setDownloading(false);
  };

  if (buttonType === "button") {
    return (
      <IonButton
        className={
          "file-attachment-button" + (cssClassName ? " " + cssClassName : "")
        }
        onClick={(e: any) => handleFileOpen(e, file)}
        fill="outline"
        size={size ? size : "default"}
      >
        {downloading ? (
          <IonSpinner name="lines-sharp-small" color="light" />
        ) : (
          <IonIcon slot="start" icon={icon ? icon : getFileIcon(file)} />
        )}
        {label ? label : file?.Name}
      </IonButton>
    );
  } else {
    return (
      <IonChip title={file?.Name}>
        {downloading ? (
          <IonSpinner name="lines-sharp-small" color="light" />
        ) : (
          <IonIcon icon={icon ? icon : getFileIcon(file)} />
        )}
        <IonLabel>
          <a
            href={file?.Url}
            target="_blank"
            onClick={(e: any) => handleFileOpen(e, file)}
          >
            {label ? label : file?.Name}
          </a>
        </IonLabel>
        {onDelete && (
          <IonIcon
            icon={closeOutline}
            onClick={() => onDelete(file)}
            title="Delete"
            aria-label="Delete"
          />
        )}
      </IonChip>
    );
  }
};

export default withRouter(FileAttachment);
